/**
 * ScadenzePage — pagina dedicata "Scadenze".
 *
 * Mostra in un'unica tabella tutte le scadenze rilevanti del personale:
 *  - Contratti TD (scaduti + in scadenza)
 *  - Documenti con scadenza (certificati, visite mediche, ecc., scaduti + in scadenza)
 *
 * Filtri:
 *  - Tipologia (tutti / contratti / documenti / per singola categoria)
 *  - Orizzonte (30 / 60 / 90 / 180 / 365 gg)
 *  - Mostra anche scaduti (default ON)
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { Button } from "../components/ui/button";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { UnilavRemindersCard } from "../components/UnilavRemindersCard";
import { ExpiredContractsWidget } from "../components/ExpiredContractsWidget";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../components/ui/table";
import {
    AlertTriangle,
    Loader2,
    FileSignature,
    FileText,
    ArrowRight,
    Calendar,
    Mail,
    Search,
    Sparkles,
    Archive,
    Printer,
} from "lucide-react";
import { toast } from "sonner";
import { CATEGORY_LABELS } from "../lib/documents";
import { Input } from "../components/ui/input";
import { SortableHead } from "../components/SortableHead";
import { useSortableTable } from "../hooks/useSortableTable";

const SORT_LABELS = {
    matricola: "matricola",
    dipendente: "dipendente",
    tipologia: "tipologia",
    dettaglio: "dettaglio",
    scadenza: "data scadenza",
    stato: "stato",
};

const HORIZONS = [
    { value: "30", label: "30 giorni" },
    { value: "60", label: "60 giorni" },
    { value: "90", label: "90 giorni" },
    { value: "180", label: "180 giorni" },
    { value: "365", label: "1 anno" },
];

const fmtDate = (iso) => {
    if (!iso) return "—";
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
    return m ? `${m[3]}/${m[2]}/${m[1]}` : iso;
};

const daysLabel = (days) => {
    if (days === 0) return "scade oggi";
    if (days === 1) return "scade domani";
    if (days < 0) return `scaduto da ${Math.abs(days)} gg`;
    return `tra ${days} gg`;
};

const daysClass = (days) => {
    if (days < 0) return "bg-red-50 text-red-700 border-red-200";
    if (days <= 30) return "bg-amber-50 text-amber-700 border-amber-200";
    return "bg-slate-50 text-slate-600 border-slate-200";
};

export default function ScadenzePage() {
    const [horizon, setHorizon] = useState("90");
    const [tab, setTab] = useState("all");
    const [includeExpired, setIncludeExpired] = useState(true);
    const [search, setSearch] = useState("");
    const [sendingSummary, setSendingSummary] = useState(false);
    const [aiRunning, setAiRunning] = useState(false);
    const [aiProgress, setAiProgress] = useState(null);
    const [reloadKey, setReloadKey] = useState(0);
    const [data, setData] = useState(null);
    const [employees, setEmployees] = useState({});
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            setLoading(true);
            try {
                const [scadRes, empRes] = await Promise.all([
                    api.get("/documents/alerts/expiring", {
                        params: { days_documents: horizon, days_contracts: horizon },
                    }),
                    api.get("/employees"),
                ]);
                setData(scadRes.data);
                const map = {};
                (empRes.data || []).forEach((e) => {
                    map[e.id] = e;
                });
                setEmployees(map);
            } catch (e) {
                setData(null);
            } finally {
                setLoading(false);
            }
        })();
    }, [horizon, reloadKey]);

    const runAiMissing = async () => {
        setAiRunning(true);
        try {
            const { data } = await api.post("/documents/extract-expiry-batch", { all_missing: true });
            if (!data.total) {
                toast.info("Nessun documento senza scadenza da analizzare (visite mediche, certificati, documenti identità)");
                return;
            }
            toast.info(`Analisi AI avviata su ${data.total} documenti — puoi continuare a lavorare`);
            let job = data;
            for (let i = 0; i < 600 && job.status !== "done"; i++) {
                await new Promise((r) => setTimeout(r, 3000));
                job = (await api.get(`/documents/extract-expiry-jobs/${data.job_id}`)).data;
                setAiProgress(job);
            }
            toast.success(`Completato: ${job.updated} scadenze compilate, ${job.not_found} senza data nel file${job.errors ? `, ${job.errors} errori` : ""}`);
            setReloadKey((k) => k + 1);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Errore analisi AI");
        } finally {
            setAiRunning(false);
            setAiProgress(null);
        }
    };

    // Build a flat list of rows ({kind: "contract"|"document", ...meta})
    const baseRows = useMemo(() => {
        if (!data) return [];
        const list = [];
        if (includeExpired) {
            (data.expired_contracts || []).forEach((c) =>
                list.push({ kind: "contract", scope: "expired", ...c }),
            );
            (data.expired_documents || []).forEach((d) =>
                list.push({ kind: "document", scope: "expired", ...d }),
            );
        }
        (data.expiring_contracts || []).forEach((c) =>
            list.push({ kind: "contract", scope: "expiring", ...c }),
        );
        (data.expiring_documents || []).forEach((d) =>
            list.push({ kind: "document", scope: "expiring", ...d }),
        );

        // Filter by tab
        let filtered = list;
        if (tab === "contracts") filtered = filtered.filter((r) => r.kind === "contract");
        else if (tab === "documents") filtered = filtered.filter((r) => r.kind === "document");
        else if (tab.startsWith("cat:")) {
            const cat = tab.slice(4);
            filtered = filtered.filter((r) => r.kind === "document" && r.category === cat);
        }

        // Search filter
        if (search.trim()) {
            const q = search.trim().toLowerCase();
            filtered = filtered.filter((r) => {
                const emp = employees[r.employee_id];
                const empName = emp ? `${emp.cognome} ${emp.nome}`.toLowerCase() : "";
                const docName = (r.filename || "").toLowerCase();
                const tipo = (r.contratto_tipo || r.category || "").toLowerCase();
                return empName.includes(q) || docName.includes(q) || tipo.includes(q);
            });
        }

        // Default order: urgency (days_left ascending); column headers override it
        filtered.sort((a, b) => a.days_left - b.days_left);
        return filtered;
    }, [data, tab, includeExpired, search, employees]);

    const tipologiaOf = (r) => (r.kind === "contract" ? "Contratto TD" : CATEGORY_LABELS[r.category] || r.category);
    const dettaglioOf = (r) => (r.kind === "contract" ? r.contratto_tipo || "Tempo determinato" : r.filename || "");

    const { sortedItems: rows, sortKey, sortDir, requestSort } = useSortableTable(baseRows, {
        customAccessors: {
            matricola: (r) => employees[r.employee_id]?.matricola || null,
            dipendente: (r) => {
                const emp = employees[r.employee_id];
                return emp ? `${emp.cognome} ${emp.nome}`.toLowerCase() : null;
            },
            tipologia: tipologiaOf,
            dettaglio: dettaglioOf,
            scadenza: (r) => r.scadenza || r.expires_at || null,
            stato: (r) => r.days_left,
        },
    });

    const sortLabel = sortKey
        ? `${SORT_LABELS[sortKey]} ${sortDir === "desc" ? "decrescente" : "crescente"}`
        : "urgenza scadenza";

    const printRows = () => {
        const esc = (s) => String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
        const tabLabel = tab === "all" ? "Tutte" : tab === "contracts" ? "Contratti TD" : tab === "documents" ? "Documenti" : (CATEGORY_LABELS[tab.slice(4)] || tab.slice(4));
        const horizonLabel = HORIZONS.find((h) => h.value === horizon)?.label || horizon;
        const body = rows.map((r, i) => {
            const emp = employees[r.employee_id];
            return `<tr>
                <td>${i + 1}</td>
                <td>${esc(emp?.matricola || "—")}</td>
                <td>${esc(emp ? `${emp.cognome} ${emp.nome}` : "—")}</td>
                <td>${esc(tipologiaOf(r))}</td>
                <td>${esc(dettaglioOf(r))}</td>
                <td>${fmtDate(r.scadenza || r.expires_at)}</td>
                <td>${daysLabel(r.days_left)}</td>
            </tr>`;
        }).join("");
        const html = `<!DOCTYPE html><html lang="it"><head><meta charset="utf-8"><title>Scadenze — Splendor Sud</title>
<style>
  body { font-family: Arial, sans-serif; font-size: 12px; color: #111; margin: 24px; }
  h1 { font-size: 18px; margin: 0 0 2px; color: #15498F; }
  .meta { color: #555; font-size: 11px; margin-bottom: 14px; }
  table { width: 100%; border-collapse: collapse; }
  th, td { border: 1px solid #bbb; padding: 4px 6px; text-align: left; vertical-align: top; }
  th { background: #eef2f7; font-size: 10px; text-transform: uppercase; }
  tr { page-break-inside: avoid; }
</style></head><body>
<h1>Scadenze — Splendor Sud S.r.l.</h1>
<div class="meta">Stampato il ${new Date().toLocaleDateString("it-IT")} · Filtro: ${esc(tabLabel)} · Orizzonte: ${horizonLabel} · ${includeExpired ? "inclusi scaduti" : "esclusi scaduti"} · Ordinamento: ${esc(sortLabel)} · ${rows.length} voci</div>
<table><thead><tr><th>#</th><th>Matricola</th><th>Dipendente</th><th>Tipologia</th><th>Dettaglio</th><th>Scadenza</th><th>Stato</th></tr></thead>
<tbody>${body}</tbody></table>
<script>window.onload = function(){ window.print(); };</script>
</body></html>`;
        const url = URL.createObjectURL(new Blob([html], { type: "text/html" }));
        const w = window.open(url, "_blank");
        if (!w) { URL.revokeObjectURL(url); toast.error("Sblocca i popup del browser per stampare"); return; }
        setTimeout(() => URL.revokeObjectURL(url), 60000);
    };

    // Build counts for tabs
    const counts = useMemo(() => {
        const all = data
            ? (includeExpired ? data.expired_contracts?.length || 0 : 0) +
              (includeExpired ? data.expired_documents?.length || 0 : 0) +
              (data.expiring_contracts?.length || 0) +
              (data.expiring_documents?.length || 0)
            : 0;
        const contracts = data
            ? (includeExpired ? data.expired_contracts?.length || 0 : 0) +
              (data.expiring_contracts?.length || 0)
            : 0;
        const documents = data
            ? (includeExpired ? data.expired_documents?.length || 0 : 0) +
              (data.expiring_documents?.length || 0)
            : 0;
        // per-category document counts
        const byCat = {};
        if (data) {
            const docs = [
                ...(includeExpired ? data.expired_documents || [] : []),
                ...(data.expiring_documents || []),
            ];
            docs.forEach((d) => {
                byCat[d.category] = (byCat[d.category] || 0) + 1;
            });
        }
        return { all, contracts, documents, byCat };
    }, [data, includeExpired]);

    return (
        <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-8" data-testid="scadenze-page">
            {/* Header */}
            <div className="mb-6 flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h1 className="flex items-center gap-2 font-display text-3xl font-bold text-[#376DB3]">
                        <AlertTriangle className="h-7 w-7 text-amber-500" />
                        Scadenze
                    </h1>
                    <p className="mt-1 text-sm text-slate-600">
                        Contratti a tempo determinato e documenti con scadenza — scaduti e in scadenza.
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={printRows}
                        disabled={loading || rows.length === 0}
                        className="h-9 gap-1.5 text-xs"
                        data-testid="print-scadenze-btn"
                        title="Stampa l'elenco delle scadenze con i filtri e l'ordinamento correnti"
                    >
                        <Printer className="h-3.5 w-3.5" />
                        Stampa
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={runAiMissing}
                        disabled={aiRunning}
                        className="h-9 gap-1.5 border-violet-200 bg-violet-50 text-xs text-violet-700 hover:bg-violet-100"
                        data-testid="ai-expiry-missing-btn"
                        title="Analizza con l'AI visite mediche, certificati e documenti d'identità senza data di scadenza e compila la scadenza trovata nel file"
                    >
                        {aiRunning ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Sparkles className="h-3.5 w-3.5" />}
                        {aiRunning
                            ? (aiProgress ? `Analisi ${aiProgress.processed}/${aiProgress.total}…` : "Avvio analisi…")
                            : "Rileva scadenze con AI"}
                    </Button>
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={async () => {
                            setSendingSummary(true);
                            try {
                                const { data } = await api.post("/documents/alerts/expiring/email?days=30");
                                if (data.count === 0) toast.info("Nessun documento in scadenza: email non inviata");
                                else if (data.sent) toast.success(`Riepilogo inviato via email (${data.count} documenti)`);
                                else toast.error("Invio non riuscito: verifica la configurazione email");
                            } catch (e) {
                                toast.error(e.response?.data?.detail || "Errore invio riepilogo");
                            } finally {
                                setSendingSummary(false);
                            }
                        }}
                        disabled={sendingSummary}
                        className="h-9 gap-1.5 text-xs"
                        data-testid="send-expiring-summary-btn"
                    >
                        {sendingSummary ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                        Invia riepilogo email
                    </Button>
                    <Calendar className="h-4 w-4 text-slate-500" />
                    <span className="text-xs text-slate-500">Orizzonte:</span>
                    <Select value={horizon} onValueChange={setHorizon}>
                        <SelectTrigger className="h-9 w-36" data-testid="scadenze-horizon">
                            <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                            {HORIZONS.map((h) => (
                                <SelectItem key={h.value} value={h.value}>{h.label}</SelectItem>
                            ))}
                        </SelectContent>
                    </Select>
                </div>
            </div>

            {/* UNILAV mandatory communications */}
            <UnilavRemindersCard />

            {/* Contratti scaduti: cessa / proroga / trasforma direttamente da qui */}
            <div className="mb-6">
                <ExpiredContractsWidget />
            </div>

            {/* KPI strip */}
            <div className="mb-5 grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Kpi label="Totale" value={counts.all} accent="amber" testid="kpi-all" />
                <Kpi label="Contratti TD" value={counts.contracts} accent="blue" testid="kpi-contracts" />
                <Kpi label="Documenti" value={counts.documents} accent="emerald" testid="kpi-documents" />
                <Kpi
                    label="Scaduti"
                    value={(data?.expired_contracts?.length || 0) + (data?.expired_documents?.length || 0)}
                    accent="red"
                    testid="kpi-expired"
                />
            </div>

            {/* Filters */}
            <div className="mb-4 flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 bg-white p-3 shadow-sm">
                <Tabs value={tab} onValueChange={setTab} className="overflow-x-auto">
                    <TabsList data-testid="scadenze-tabs">
                        <TabsTrigger value="all" data-testid="tab-all">
                            Tutti ({counts.all})
                        </TabsTrigger>
                        <TabsTrigger value="contracts" data-testid="tab-contracts">
                            Contratti TD ({counts.contracts})
                        </TabsTrigger>
                        <TabsTrigger value="documents" data-testid="tab-documents">
                            Documenti ({counts.documents})
                        </TabsTrigger>
                        {Object.keys(counts.byCat)
                            .sort((a, b) => (counts.byCat[b] || 0) - (counts.byCat[a] || 0))
                            .map((c) => (
                                <TabsTrigger key={c} value={`cat:${c}`} data-testid={`tab-cat-${c}`}>
                                    {CATEGORY_LABELS[c] || c} ({counts.byCat[c]})
                                </TabsTrigger>
                            ))}
                    </TabsList>
                </Tabs>
                <div className="relative flex-1 sm:max-w-xs">
                    <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-slate-400" />
                    <Input
                        type="search"
                        placeholder="Cerca dipendente, documento, tipo…"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="h-9 pl-8 text-sm"
                        data-testid="scadenze-search"
                    />
                </div>
                <label className="inline-flex cursor-pointer items-center gap-2 text-sm text-slate-700">
                    <input
                        type="checkbox"
                        checked={includeExpired}
                        onChange={(e) => setIncludeExpired(e.target.checked)}
                        className="h-4 w-4 rounded border-slate-300 text-[#376DB3] focus:ring-[#376DB3]"
                        data-testid="scadenze-include-expired"
                    />
                    Includi scaduti
                </label>
                <span className="text-xs text-slate-400" data-testid="scadenze-sort-hint">
                    Ordinamento: <span className="font-medium text-slate-600">{sortLabel}</span>
                    {" "}· clicca le intestazioni per cambiarlo
                </span>
            </div>

            {/* Table */}
            <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
                {loading ? (
                    <div className="flex h-48 items-center justify-center">
                        <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                    </div>
                ) : rows.length === 0 ? (
                    <div className="px-6 py-12 text-center text-sm text-slate-500">
                        <div className="mb-2 text-3xl">✓</div>
                        Nessuna scadenza con i filtri selezionati.
                    </div>
                ) : (
                    <div className="overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-slate-50">
                                    <SortableHead sortKey="matricola" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="w-24 font-semibold">
                                        Matricola
                                    </SortableHead>
                                    <SortableHead sortKey="dipendente" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="font-semibold">
                                        Dipendente
                                    </SortableHead>
                                    <SortableHead sortKey="tipologia" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="font-semibold">
                                        Tipologia
                                    </SortableHead>
                                    <SortableHead sortKey="dettaglio" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="font-semibold">
                                        Dettaglio
                                    </SortableHead>
                                    <SortableHead sortKey="scadenza" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="font-semibold">
                                        Scadenza
                                    </SortableHead>
                                    <SortableHead sortKey="stato" sortKeyCurrent={sortKey} sortDir={sortDir} onClick={requestSort} className="font-semibold">
                                        Stato
                                    </SortableHead>
                                    <TableHead className="text-right font-semibold">Azione</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {rows.map((r, idx) => {
                                    const emp = employees[r.employee_id];
                                    return (
                                        <TableRow
                                            key={`${r.kind}-${r.id || r.employee_id}-${idx}`}
                                            data-testid={`scad-row-${idx}`}
                                        >
                                            <TableCell>
                                                <span className="inline-flex rounded bg-slate-100 px-2 py-0.5 font-mono-numbers text-xs font-semibold text-slate-700">
                                                    {emp?.matricola || "—"}
                                                </span>
                                            </TableCell>
                                            <TableCell>
                                                {emp ? (
                                                    <Link
                                                        to={`/dipendenti/${r.employee_id}`}
                                                        className="font-medium text-[#376DB3] hover:underline"
                                                    >
                                                        {emp.cognome} {emp.nome}
                                                    </Link>
                                                ) : (
                                                    <span className="text-slate-400">—</span>
                                                )}
                                            </TableCell>
                                            <TableCell>
                                                {r.kind === "contract" ? (
                                                    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded border border-blue-200 bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-700">
                                                        <FileSignature className="h-3 w-3" />
                                                        Contratto TD
                                                    </span>
                                                ) : (
                                                    <span className="inline-flex items-center gap-1 whitespace-nowrap rounded border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-xs font-semibold text-emerald-700">
                                                        <FileText className="h-3 w-3" />
                                                        {CATEGORY_LABELS[r.category] || r.category}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className="max-w-xs">
                                                {r.kind === "contract" ? (
                                                    <span className="text-sm text-slate-600">
                                                        {r.contratto_tipo || "Tempo determinato"}
                                                    </span>
                                                ) : (
                                                    <span className="block truncate text-sm text-slate-600" title={r.filename}>
                                                        {r.filename}
                                                    </span>
                                                )}
                                            </TableCell>
                                            <TableCell className="font-mono-numbers text-sm text-slate-700">
                                                {fmtDate(r.scadenza || r.expires_at)}
                                            </TableCell>
                                            <TableCell>
                                                <span className={`inline-flex whitespace-nowrap rounded border px-2 py-0.5 text-xs font-semibold ${daysClass(r.days_left)}`}>
                                                    {daysLabel(r.days_left)}
                                                </span>
                                            </TableCell>
                                            <TableCell className="text-right">
                                                <Link to={`/dipendenti/${r.employee_id}`}>
                                                    <Button variant="ghost" size="sm" className="gap-1">
                                                        Apri <ArrowRight className="h-3 w-3" />
                                                    </Button>
                                                </Link>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </div>
                )}
            </div>

            <RenewalsCleanupCard />
        </div>
    );
}

function RenewalsCleanupCard() {
    const [loading, setLoading] = useState(false);
    const [items, setItems] = useState(null);
    const [archiving, setArchiving] = useState(false);

    const analyze = async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/documents/renewals/cleanup-preview");
            setItems(data.items);
            if (data.count === 0) toast.success("Nessun vecchio documento da archiviare: tutto pulito!");
        } catch {
            toast.error("Errore nell'analisi dei rinnovi");
        } finally {
            setLoading(false);
        }
    };

    const archiveAll = async () => {
        if (!window.confirm(`Archiviare ${items.length} vecchi documenti già rinnovati?\nSpariranno dai fascicoli (restano nello storico operazioni).`)) return;
        setArchiving(true);
        try {
            const { data } = await api.post("/documents/renewals/cleanup");
            toast.success(`${data.archived} vecchi documenti archiviati`);
            setItems([]);
        } catch {
            toast.error("Errore nell'archiviazione");
        } finally {
            setArchiving(false);
        }
    };

    return (
        <div className="mt-6 rounded-lg border border-slate-200 bg-white p-5 shadow-sm" data-testid="renewals-cleanup-card">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-md bg-[#376DB3]/10 text-[#376DB3]">
                        <Archive className="h-5 w-5" />
                    </div>
                    <div>
                        <div className="text-sm font-semibold text-slate-800">Pulizia documenti rinnovati</div>
                        <p className="text-xs text-slate-500">
                            Trova i vecchi documenti scaduti che sono già stati sostituiti da un rinnovo e archiviali in un click.
                        </p>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={analyze}
                        disabled={loading}
                        className="gap-1.5"
                        data-testid="renewals-analyze-btn"
                    >
                        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                        Analizza
                    </Button>
                    {items && items.length > 0 && (
                        <Button
                            size="sm"
                            onClick={archiveAll}
                            disabled={archiving}
                            className="gap-1.5 bg-[#376DB3] text-white hover:bg-[#376DB3]/90"
                            data-testid="renewals-archive-btn"
                        >
                            {archiving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Archive className="h-4 w-4" />}
                            Archivia {items.length} documenti
                        </Button>
                    )}
                </div>
            </div>
            {items && items.length > 0 && (
                <ul className="mt-4 max-h-72 space-y-1.5 overflow-y-auto" data-testid="renewals-preview-list">
                    {items.map((it) => (
                        <li key={it.id} className="flex flex-wrap items-center gap-x-2 gap-y-0.5 rounded-md border border-slate-100 bg-slate-50 px-3 py-1.5 text-xs">
                            <Link to={`/dipendenti/${it.employee_id}`} className="font-semibold text-[#376DB3] hover:underline">
                                {it.employee_name}
                            </Link>
                            <span className="text-slate-600" title={it.filename}>{it.filename}</span>
                            <span className="text-red-600">(scad. {fmtDate(it.expires_at)})</span>
                            <ArrowRight className="h-3 w-3 text-slate-400" />
                            <span className="text-slate-600" title={it.replaced_by}>{it.replaced_by}</span>
                            <span className="text-emerald-700">(scad. {fmtDate(it.replaced_by_expires)})</span>
                        </li>
                    ))}
                </ul>
            )}
            {items && items.length === 0 && (
                <div className="mt-4 rounded-md border border-emerald-100 bg-emerald-50 px-3 py-2 text-xs text-emerald-700" data-testid="renewals-empty">
                    Nessun vecchio documento da archiviare.
                </div>
            )}
        </div>
    );
}

function Kpi({ label, value, accent, testid }) {
    const palette = {
        amber: "border-amber-200 bg-amber-50 text-amber-800",
        blue: "border-blue-200 bg-blue-50 text-blue-800",
        emerald: "border-emerald-200 bg-emerald-50 text-emerald-800",
        red: "border-red-200 bg-red-50 text-red-800",
    }[accent || "amber"];
    return (
        <div className={`rounded-lg border p-4 shadow-sm ${palette}`} data-testid={testid}>
            <div className="text-[11px] font-semibold uppercase tracking-wide opacity-80">{label}</div>
            <div className="font-display font-mono-numbers text-2xl font-bold">{value}</div>
        </div>
    );
}
